import { type FormEvent, useState } from 'react';
import { ErrorApi, useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta } from '../formato';
import type { Vehiculo } from '../paginas/tipos';

export interface Subasta {
  id: string;
  nombre: string;
  pais: string;
  moneda: 'USD' | 'MXN';
}
interface Compra {
  id: string;
  subasta: string;
  numeroLote: string;
  urlLote: string | null;
  fechaCompra: string;
  moneda: string;
  precioMartillo: string;
  cargos: { concepto: string; cantidad: string; estimado?: boolean }[];
}

const hoy = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format();
const dinero = (moneda: string, cantidad: string) =>
  `${moneda} ${Number(cantidad).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Compra en subasta (módulo Auctions, modo 1: registrar lo que ya se compró). Al guardarla,
 * Inventario pasa el auto a «comprado» y Finanzas registra la adquisición (VehicleAcquired).
 */
export function CompraSubasta({ v, alGuardar }: { v: Vehiculo; alGuardar: () => void }) {
  const api = useApi();
  const compra = useConsulta<Compra>(`/sourcing/compras/${v.id}`);
  const { datos: subastas } = useConsulta<Subasta[]>('/sourcing/subastas');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const sinCompra = compra.estadoError === 404;

  const enviar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const cargos = String(f.get('cargos') ?? '')
      .split('\n')
      .map((l) => l.split(/[:=]/).map((x) => x.trim()))
      .filter(([c, m]) => c && m)
      .map(([concepto, cantidad]) => ({
        concepto: concepto as string,
        cantidad: String(cantidad).replace(/[$,\s]/g, ''),
      }));
    setEnviando(true);
    setError(null);
    try {
      await api('POST', '/sourcing/compras', {
        vehiculoId: v.id,
        vin: v.vin,
        subasta: f.get('subasta'),
        numeroLote: f.get('numeroLote'),
        ...(f.get('urlLote') && { urlLote: f.get('urlLote') }),
        fechaCompra: f.get('fechaCompra'),
        precioMartillo: String(f.get('precioMartillo')),
        ...(cargos.length && { cargos }),
        diasAlmacenaje: Number(f.get('diasAlmacenaje') || 0),
      });
      compra.recargar();
      // Los eventos tardan unos segundos en llegar a Inventario y Finanzas.
      setTimeout(alGuardar, 2500);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo registrar la compra');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className="seccion">
      <header>
        <h2>Compra en subasta</h2>
      </header>
      {compra.datos ? (
        <div className="cuerpo">
          <dl className="resumen">
            <div>
              <dt>Subasta y lote</dt>
              <dd>
                {subastas?.find((s) => s.id === compra.datos?.subasta)?.nombre ??
                  compra.datos.subasta}{' '}
                · lote{' '}
                {compra.datos.urlLote ? (
                  <a href={compra.datos.urlLote} target="_blank" rel="noreferrer">
                    {compra.datos.numeroLote}
                  </a>
                ) : (
                  compra.datos.numeroLote
                )}
              </dd>
            </div>
            <div>
              <dt>Fecha</dt>
              <dd>{fechaCorta(compra.datos.fechaCompra)}</dd>
            </div>
            <div>
              <dt>Precio de martillo</dt>
              <dd>{dinero(compra.datos.moneda, compra.datos.precioMartillo)}</dd>
            </div>
            {compra.datos.cargos.map((c) => (
              <div key={c.concepto}>
                <dt>
                  {c.concepto}
                  {c.estimado ? ' (estimado)' : ''}
                </dt>
                <dd>{dinero(compra.datos?.moneda ?? 'USD', c.cantidad)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : compra.cargando ? (
        <p className="vacio">Cargando…</p>
      ) : sinCompra ? (
        <form className="cuerpo formulario" onSubmit={(e) => void enviar(e)}>
          <label className="campo">
            <span>Subasta</span>
            <select name="subasta" defaultValue="copart">
              {(subastas ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre} ({s.moneda})
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Número de lote</span>
            <input name="numeroLote" required maxLength={40} />
          </label>
          <label className="campo">
            <span>Liga del lote (opcional)</span>
            <input name="urlLote" type="url" />
          </label>
          <label className="campo">
            <span>Fecha de compra</span>
            <input name="fechaCompra" type="date" required defaultValue={hoy()} />
          </label>
          <label className="campo">
            <span>Precio de martillo</span>
            <input
              name="precioMartillo"
              required
              inputMode="decimal"
              pattern="\d{1,12}(\.\d{1,2})?"
            />
          </label>
          <label className="campo">
            <span>Días de almacenaje</span>
            <input name="diasAlmacenaje" type="number" min={0} max={365} defaultValue={0} />
          </label>
          <label className="campo" style={{ gridColumn: '1 / -1' }}>
            <span>
              Cargos de la factura, uno por renglón «Concepto: cantidad» (si los dejas vacíos se
              estiman con la tabla vigente de la subasta)
            </span>
            <textarea name="cargos" rows={3} placeholder={'Buyer fee: 610\nGate fee: 79'} />
          </label>
          {error && (
            <p className="error" style={{ gridColumn: '1 / -1' }}>
              {error}
            </p>
          )}
          <div className="acciones">
            <button className="boton primario" disabled={enviando}>
              {enviando ? 'Registrando…' : 'Registrar compra'}
            </button>
          </div>
        </form>
      ) : (
        <p className="error" style={{ margin: 16 }}>
          {compra.error}
        </p>
      )}
    </section>
  );
}
